import React, { useState, useEffect } from "react";
import Button from "./Button";
import { useStateContext } from "../contexts/ContextProvider";
import ProgressBar from "./ProgressBar";
import { apiCreateChore } from "../services/apiService";
import {
  DayOfMonthPicker,
  DayOfWeekPicker,
  FirstBiWeeklyPicker,
  SecondBiWeeklyPicker,
} from "./helpers";
import { getDayOfTheWeek, getOrdinalSuffix } from "../helpers/helperFunctions";

const initialErrorState = {
  choreName: "",
  choreDesc: "",
  frequency: "",
  rooms: "",
};

const descLength = 300;

const AddChoreModal = (props) => {
  const { currentProperty } = props;
  const { leases, setChores, handleModal } = useStateContext();
  const [error, setError] = useState(initialErrorState);
  const [count, setCount] = useState(descLength);
  const [page, setPage] = useState(1);
  const pages = 5;

  let initialCheckboxStates = {};
  let leasesArr = [];

  const currentPropertyLeases = leases.filter(
    (x) => x.propertyId === currentProperty.id
  );

  currentPropertyLeases.forEach((x) => {
    initialCheckboxStates[x.id] = {
      id: x.id,
      roomName: x.roomName,
      isSelected: true,
    };
  });

  const [selectedLeases, setSelectedLeases] = useState(initialCheckboxStates);

  const [chore, setChore] = useState({
    id: (Math.random() * 1000) | 20, // random id between 20 - 1000 * must be unique
    landlordId: currentProperty.landlordId,
    propertyId: currentProperty.id,
    choreName: "",
    choreDesc: "",
    frequency: "--", // daily, weekly, bi-weekly, monthly
    firstActionDay: 1,
    secondActionDay: "",
    leaseIds: [],
  });

  console.log(chore);

  useEffect(() => {
    if (page === 4) {
      populateLeasesArr();
    }
    // eslint-disable-next-line
  }, [selectedLeases]);

  const nextPage = async () => {
    if (page === 3) populateLeasesArr();
    if (hasErrors()) {
      return;
    } else {
      setPage(page + 1);
    }
  };

  const prevPage = () => {
    setPage(page - 1);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "frequency" && value === "daily") {
      setChore((prevState) => ({
        ...prevState,
        firstActionDay: "0",
        secondActionDay: "",
      }));
    }
    if (name === "frequency" && (value === "weekly" || value === "monthly")) {
      setChore((prevState) => ({
        ...prevState,
        secondActionDay: "",
      }));
    }
    if (name === "frequency" && value === "bi-weekly") {
      setChore((prevState) => ({
        ...prevState,
        secondActionDay: 15,
      }));
    }

    setChore((prevState) => ({ ...prevState, [name]: value }));
    if (name === "choreDesc") setCount(descLength - value.length);
  };

  const handleChecked = (event) => {
    const { id, checked } = event.target;

    setSelectedLeases((prevState) => ({
      ...prevState,
      [id]: { ...prevState[id], isSelected: checked },
    }));
  };

  const populateLeasesArr = () => {
    Object.entries(selectedLeases).forEach(([key, value]) => {
      if (value.isSelected) leasesArr.push(value.id);
    });
    setChore((prevState) => ({ ...prevState, leaseIds: leasesArr }));
  };

  const addChore = async () => {
    const response = await apiCreateChore(chore);
    if (response.hasError) {
      console.log(response.error.response.statusText);
      return;
    } else {
      setChores((prevState) => [...prevState, response.data]);
      handleModal("addChore");
    }
  };

  const hasErrors = () => {
    if (page === 2 && chore.choreName === "") {
      activateError("choreName");
      return true;
    } else if (page === 2 && chore.choreDesc === "") {
      activateError("choreDesc");
      return true;
    } else if (page === 3 && chore.frequency === "--") {
      activateError("frequency");
      return true;
    } else if (page === 4 && chore.leaseIds.length === 0) {
      activateError("rooms");
      return true;
    } else {
      return false;
    }
  };

  const activateError = (item) => {
    setError((prevState) => ({
      ...prevState,
      [item]: "animate-shake text-red-600 border-red-600",
    }));
    setTimeout(() => {
      setError((prevState) => ({
        ...prevState,
        [item]: "",
      }));
    }, 500);
    clearTimeout();
  };

  return (
    <div className="w-screen h-screen fixed top-0 left-0 right-0 bg-white mt-20 z-10">
      <ProgressBar pages={pages} currentPage={page} />
      <form
        className="grid justify-items-center"
        onSubmit={(e) => {
          e.preventDefault();
          page === pages ? addChore() : nextPage();
        }}
      >
        {page === 1 && (
          <>
            <div className="flex justify-center text-lg mt-10">Add Chore</div>
            <div className="px-10">
              <div
                className="max-w-[500px] text-center bg-gray-100 border-gray-400 
                border-2 rounded-2xl mt-10 p-5"
              >
                Adding chores is a great way to split household
                responsibilities. Chores will rotatate between roommates on a
                fixed schedule.
              </div>
            </div>
          </>
        )}
        {page === 2 && (
          <>
            <div className="flex justify-center mt-10 text-lg">
              What chore would you like to add?
            </div>
            <label className={`grid mt-5 ${error.choreName}`}>
              <div>
                Chore Name: <span className="text-red-500">*</span>
              </div>
              <input
                id="chore-name"
                name="choreName"
                type="text"
                maxLength={60}
                placeholder="Name"
                value={chore.choreName}
                autoFocus={true}
                className={`w-72 border-gray-300 border-1 rounded-md p-1 ${error.choreName}`}
                onChange={handleChange}
              />
            </label>
            <label className={`grid mt-5 ${error.choreDesc}`}>
              <div>
                What does this chore consist of?{" "}
                <span className="text-red-500">*</span>
              </div>
              <textarea
                id="chore-desc"
                name="choreDesc"
                type="text"
                maxLength={descLength}
                placeholder="Description"
                value={chore.choreDesc}
                className={`w-72 h-40 border-gray-300 border-1 p-1 ${error.choreDesc}`}
                onChange={handleChange}
              />
              <div
                className={`flex justify-end ${
                  count === 0 ? "animate-shake text-red-600" : ""
                }`}
              >
                {count}
              </div>
            </label>
          </>
        )}
        {page === 3 && (
          <>
            <div
              className={`text-lg text-center mt-10 mb-5 px-10 ${error.frequency}`}
            >
              How frequently should this chore be completed?
              <span className="text-red-500">*</span>
            </div>
            <label className="grid">
              <select
                id="frequency"
                name="frequency"
                value={chore.frequency}
                className={`w-40 border-black border-1 ${error.frequency}`}
                onChange={handleChange}
                onChangeCapture={() => console.log("selecting freq")}
              >
                <option disabled>--</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="bi-weekly">Every Two Weeks</option>
                <option value="monthly">Monthly</option>
              </select>
            </label>
            {chore.frequency === "weekly" && (
              <div className="mt-10">
                <div>On:</div>
                <select
                  id="first-action-day"
                  name="firstActionDay"
                  value={chore.firstActionDay}
                  className={`w-40 border-black border-1 ${error.frequency}`}
                  onChange={handleChange}
                  onChangeCapture={() => console.log("day of month selected")}
                >
                  <DayOfWeekPicker />
                </select>
              </div>
            )}
            {chore.frequency === "bi-weekly" && (
              <div className="mt-10">
                <div>On the:</div>
                <select
                  id="first-action-day"
                  name="firstActionDay"
                  value={chore.firstActionDay}
                  className={`w-40 border-black border-1 ${error.frequency}`}
                  onChange={handleChange}
                  onChangeCapture={() => console.log("day of month selected")}
                >
                  <FirstBiWeeklyPicker />
                </select>
                <div className="mt-5">And:</div>
                <select
                  id="second-action-day"
                  name="secondActionDay"
                  value={chore.secondActionDay}
                  className={`w-40 border-black border-1 ${error.frequency}`}
                  onChange={handleChange}
                  onChangeCapture={() => console.log("day of month selected")}
                >
                  <SecondBiWeeklyPicker />
                </select>
              </div>
            )}
            {chore.frequency === "monthly" && (
              <div className="mt-10">
                <div>On the:</div>
                <select
                  id="first-action-day"
                  name="firstActionDay"
                  value={chore.firstActionDay}
                  className={`w-40 border-black border-1 ${error.frequency}`}
                  onChange={handleChange}
                  onChangeCapture={() => console.log("day of month selected")}
                >
                  <DayOfMonthPicker />
                </select>
              </div>
            )}
          </>
        )}
        {page === 4 && (
          <>
            <div className={`grid mt-10 mb-5 px-10 ${error.rooms}`}>
              Which rooms are responsible for this chore?
            </div>
            <ul className="grid grid-cols-2 gap-x-10 gap-y-5">
              {currentPropertyLeases.map((item, index) => {
                return (
                  <li
                    key={item.id}
                    className={`flex justify-between ${error.rooms}`}
                  >
                    <div>{item.roomName}: </div>
                    <input
                      id={item.id}
                      name={item.roomName}
                      type="checkbox"
                      checked={selectedLeases[item.id].isSelected}
                      className="h-5 w-5"
                      onChange={handleChecked}
                    />
                  </li>
                );
              })}
            </ul>
          </>
        )}
        {page === pages && (
          <div className="grid mt-10 mb-5 px-10">
            <div className="flex justify-center">Is everything correct?</div>
            <div
              className="grid grid-cols-2 gap-y-3 items-center border-gray-300 border-2 
            rounded-2xl mt-10 p-5 capitalize"
            >
              <div>Chore:</div>
              <div
                className="flex justify-center text-center text-blue-500 hover:text-blue-700 
                cursor-pointer"
                onClick={() => setPage(2)}
              >
                {chore.choreName}
              </div>
              <div>Frenquency:</div>
              <div
                className="flex justify-center text-blue-500 hover:text-blue-700 cursor-pointer"
                onClick={() => setPage(3)}
              >
                {chore.frequency}
              </div>
              {chore.frequency !== "daily" && (
                <>
                  <div className="normal-case">On:</div>
                  <div
                    className="flex justify-center text-blue-500 hover:text-blue-700 cursor-pointer"
                    onClick={() => setPage(3)}
                  >
                    {chore.frequency === "weekly" &&
                      getDayOfTheWeek(chore.firstActionDay)}
                    {(chore.frequency === "bi-weekly" ||
                      chore.frequency === "monthly") &&
                      (chore.firstActionDay === 0
                        ? "Last Day of Month"
                        : chore.firstActionDay +
                          getOrdinalSuffix(chore.firstActionDay))}
                    {chore.frequency === "bi-weekly" &&
                      ` & ${
                        chore.secondActionDay
                          ? chore.firstActionDay +
                            getOrdinalSuffix(chore.secondActionDay)
                          : ""
                      }`}
                  </div>
                </>
              )}
              <div>Rotation:</div>
              <ul
                className="flex flex-wrap justify-center text-center text-blue-500 hover:text-blue-700 cursor-pointer"
                onClick={() => setPage(4)}
              >
                {chore.leaseIds.map((item, index) => {
                  return (
                    <li key={index} className="px-1">
                      {
                        currentPropertyLeases.find((x) => x.id === item)
                          .roomName
                      }
                      {chore.leaseIds.length - 1 === index ? "" : ","}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </form>
      <div className="flex justify-center w-full fixed top-[525px]">
        <Button
          background="bg-slate-400"
          text="text-white"
          opacity="bg-opacity-100"
          title={page === 1 ? "Close" : "Back"}
          height="h-10"
          width="w-24"
          borderColor="border-none"
          borderHover="hover:border-snappily-orange"
          customFunc={page === 1 ? () => handleModal("addChore") : prevPage}
        />
        <Button
          background="bg-snappily-orange"
          text="text-white"
          opacity="bg-opacity-100"
          title={page === pages ? "Submit" : "Next"}
          height="h-10"
          width="w-24"
          borderColor="border-none"
          borderHover="hover:border-snappily-orange"
          customFunc={page === pages ? addChore : nextPage}
        />
      </div>
    </div>
  );
};

export default AddChoreModal;
